import React, {useState} from 'react';
import { useNavigate } from 'react-router-dom';
import { login, googleAuth } from '../../api/axios.js';
import { useUser } from '../../context/userContext.js';
import BaseLayout from '../../components/BaseLayout.js';
import Card from '../../components/Card.js';
import Title from '../../components/Title.js';
import LongInput from '../../components/LongInput.js';
import ShortButton from '../../components/ShortButton.js';
import GoogleButton from '../../components/GoogleButton.js';
import LoadingDots from '../../components/LoadingDots.js';

const Login = () => {

  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const {contextLogin}=useUser();

  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  
  const validate = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    if (!password) newErrors.password = 'Password is required';
    return newErrors;
  };

  const handleSubmitLogin = async (event) => {
    event.preventDefault();

    setError('');
    setErrors({});
  
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
  
    setLoginLoading(true);
    const loginData = { email, password };
  
    try {
      const response = await login(loginData);
      setError('');
      navigate('/dashboard');
      contextLogin(response);
    } catch (error) {
      setError(error.response?.data?.message || "There was an issue with the login. Please try again");
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleGoogleLogin = async (event) => {
    event.preventDefault();
    try {
      // Fetch the Google OAuth URL from the backend
      const response = await googleAuth();
      const { url } = response.data;
      
      // Redirect the user to the Google OAuth URL
      window.location.href = url;
    } catch (error) {
      setError('Failed to initiate Google login. Please try again.');
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgotpassword');
  };

  const handleVerifyAccount = () => {
    try {
      navigate('/send-verification');
    } catch (error) {
      setError('Failed to send verification email. Please try again.');
    }
  };

  const handleSignUp = () => {
    setSignupLoading(true);
    setTimeout(() => {
      navigate('/signup');
    }, 300);
  };

  return (
    <>
      <style>
        {`
          .form-container {
            margin: 0 auto;
            padding: 0 24%;
            display: flex;
            flex-direction: column;
            padding-top: 2%;
          }

          .card-content {
            max-height: calc(100vh - 300px);
            overflow-y: auto;
            overflow-x: hidden;
            scrollbar-width: thin;
            scrollbar-color: #6B3A8F rgba(26, 26, 26, 0.5);
            padding: 10px;
          }

          .card-content::-webkit-scrollbar {
            width: 6px;
          }

          .card-content::-webkit-scrollbar-track {
            background: rgba(26, 26, 26, 0.5);
          }

          .card-content::-webkit-scrollbar-thumb {
            background-color: #6B3A8F;
            border-radius: 3px;
            border: none;
          }

          .form-fields {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            width: 100%;
            padding-top: 4%;
          }

          .button-gap {
            margin-bottom: 12px;
          }
          
          .error-message {
            color: #ff4d4f;
            margin: 8px 0;
            text-align: center;
            width: 100%;
          }

          .links-container {
            width: 90%;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .account-link {
            color: #E1E1E6;
            font-family: 'Poppins', sans-serif;
            font-size: 12px;
            background: none;
            border: none;
            cursor: pointer;
          }

          .account-link:hover {
            color: #9797FA;
          }

          .divider-container {
            display: flex;
            align-items: center;
            gap: 16px;
            width: 100%;
            margin: 8px 0;
          }

          .divider-line {
            height: 1px;
            flex-grow: 1;
            background: #6B3A8F;
          }

          .divider-text {
            font-family: Poppins;
            font-size: 16px;
            font-style: normal;
            font-weight: 700;
            line-height: 140%;
            padding: 0 8px;
            background: conic-gradient(from 90deg at 72.69% -757.84%, #D9BAFF 0deg, #D4E8F8 183.6083436012268deg, #DAB6E0 360deg);
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
        `}
      </style>

      <BaseLayout>
        <div className="form-container">
          <Card>
            <div className="card-content">
              <Title text="Welcome!"/>
              <form onSubmit={handleSubmitLogin} className="form-fields">
                <LongInput 
                  text="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={errors.email}
                  identifier="email-input"
                />
                <LongInput 
                  text="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={errors.password}
                  identifier="password-input"
                />

                {error && <div className="error-message">{error}</div>}
                
                <div className="links-container">
                  <button
                    className="account-link" 
                    onClick={handleVerifyAccount}
                    type="button"
                    data-cy="verifyaccount-button">
                      Verify Account?
                  </button>
                  <button
                    className="account-link" 
                    onClick={handleForgotPassword}
                    type="button"
                    data-cy="forgotpassword-button">
                      Forgot Password?
                  </button>
                </div>
                
                <div className="button-gap"><ShortButton text={loginLoading ? <LoadingDots /> : "Login"} type="submit" onClick={handleSubmitLogin} disabled={loginLoading} identifier="login-button"/></div>
                <div className="button-gap"><GoogleButton text="Login" onClick={handleGoogleLogin} identifier="googlelogin-button"/></div>

                <div className="divider-container">
                  <div className="divider-line" />
                  <span className="divider-text">or</span>
                  <div className="divider-line" />
                </div>

                <ShortButton text={signupLoading ? <LoadingDots /> : "Sign Up"}  onClick={handleSignUp} disabled={signupLoading} identifier="signup-button"/>
              </form>
            </div>
          </Card>
        </div>
      </BaseLayout>
    </>
  );
};

export default Login;